import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faSpinner,
  faCheck,
  faTimes,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { 
  getAllBusinessUsers, 
  createBusinessUser, 
  updateBusinessUser, 
  deleteBusinessUser,
  getBusinessUserReport,
  RegisterBusinessUserRequest,
  UpdateBusinessUserRequest
} from '../../services/adminApi.ts';
import { getBusinessPlans } from '../../services/api.ts';
import { BusinessPlan } from '../../types/business.ts';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AddButton = styled.button`
  background-color: #2ecc71;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #27ae60;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableHead = styled.thead`
  background-color: #f5f7fa;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9f9f9;
  }
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const TableHeader = styled.th`
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
`;

const TableCell = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;

const ActionCell = styled(TableCell)`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 4px;
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const EditButton = styled(ActionButton)`
  color: #3498db;
  
  &:hover {
    color: #2980b9;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #e74c3c;
  
  &:hover {
    color: #c0392b;
  }
`;

const ReportButton = styled(ActionButton)`
  color: #9b59b6;
  
  &:hover {
    color: #8e44ad;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  padding: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #7f8c8d;
  
  &:hover {
    color: #34495e;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #34495e;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  
  input {
    margin-right: 8px;
  }
`;

const SubmitButton = styled.button`
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 10px;
  
  &:hover {
    background-color: #2980b9;
  }
  
  &:disabled {
    background-color: #95a5a6;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 1.2rem;
  color: #7f8c8d;
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  margin-right: 10px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const StatusBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  background-color: ${props => props.active ? '#e1f5fe' : '#ffebee'};
  color: ${props => props.active ? '#0288d1' : '#c62828'};
  
  svg {
    margin-right: 4px;
  }
`;

const ConfirmationModal = styled(Modal)``;

const ConfirmationContent = styled(ModalContent)`
  max-width: 400px;
`;

const ConfirmationTitle = styled.h3`
  margin-top: 0;
  color: #e74c3c;
`;

const ConfirmationText = styled.p`
  margin-bottom: 20px;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const CancelButton = styled.button`
  background-color: #95a5a6;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  
  &:hover {
    background-color: #7f8c8d;
  }
`;

const ConfirmButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  
  &:hover {
    background-color: #c0392b;
  }
`;

interface BusinessUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  phoneNumber?: string;
  planId: string;
  plan: {
    name: string;
  };
  isActive: boolean;
  createdAt: string;
  subscriptionStartDate: string;
  subscriptionEndDate?: string;
}

const BusinessUsersPage: React.FC = () => {
  const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);
  const [businessPlans, setBusinessPlans] = useState<BusinessPlan[]>([]);
  const [businessPlanMapping, setBusinessPlanMapping] = useState<Record<string, BusinessPlan>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<BusinessUser | null>(null);
  const [userToDelete, setUserToDelete] = useState<BusinessUser | null>(null);
  const [formData, setFormData] = useState<Partial<RegisterBusinessUserRequest>>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    companyName: '',
    phoneNumber: '',
    planId: ''
  });
  
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchBusinessUsers();
    fetchBusinessPlans();
  }, []);
  
  const fetchBusinessUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllBusinessUsers();
      setBusinessUsers(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching business users:', err);
      setError(err.response?.data?.message || 'Failed to load business users');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBusinessPlans = async () => {
    try {
      const plans = await getBusinessPlans();
      setBusinessPlans(plans);
      
      // Create a mapping of plan id to plan object for easy lookup
      const mapping: Record<string, BusinessPlan> = {};
      plans.forEach(plan => {
        mapping[plan.id] = plan;
      });
      setBusinessPlanMapping(mapping);
    } catch (error) {
      console.error('Error fetching business plans:', error);
    }
  };
  
  const handleOpenModal = (user: BusinessUser | null = null) => {
    if (user) {
      setCurrentUser(user);
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        companyName: user.companyName,
        phoneNumber: user.phoneNumber || '',
        planId: user.planId
      });
    } else {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        companyName: '',
        phoneNumber: '',
        planId: businessPlans.length > 0 ? businessPlans[0].id : ''
      });
    }
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentUser(null);
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (currentUser) {
        // Update existing user
        await updateBusinessUser(currentUser.id, formData as UpdateBusinessUserRequest);
      } else {
        // Create new user
        if (!formData.password) {
          setError('Password is required for new users');
          return;
        }
        await createBusinessUser(formData as RegisterBusinessUserRequest);
      }
      
      handleCloseModal();
      fetchBusinessUsers();
    } catch (err: any) {
      console.error('Error saving business user:', err);
      setError(err.response?.data?.message || 'Failed to save business user');
    }
  };
  
  const handleDeleteClick = (user: BusinessUser) => {
    setUserToDelete(user);
    setIsConfirmModalOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    
    try {
      await deleteBusinessUser(userToDelete.id);
      setIsConfirmModalOpen(false);
      fetchBusinessUsers();
    } catch (err: any) {
      console.error('Error deleting business user:', err);
      setError(err.response?.data?.message || 'Failed to delete business user');
    }
  };
  
  const handleViewReport = async (userId: string) => {
    navigate(`/admin/reports/business-user/${userId}`);
  };
  
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingIcon icon={faSpinner} />
        Loading business users...
      </LoadingContainer>
    );
  }
  
  return (
    <>
      <PageHeader>
        <h1>Business User Management</h1>
        <AddButton onClick={() => handleOpenModal()}>
          <FontAwesomeIcon icon={faPlus} />
          Add Business User
        </AddButton>
      </PageHeader>
      
      {error && <ErrorContainer>{error}</ErrorContainer>}
      
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Company</TableHeader>
            <TableHeader>Name</TableHeader>
            <TableHeader>Email</TableHeader>
            <TableHeader>Plan</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Subscription</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <tbody>
          {businessUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell>{user.companyName}</TableCell>
              <TableCell>{user.firstName} {user.lastName}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.plan.name}</TableCell>
              <TableCell>
                <StatusBadge active={user.isActive}>
                  <FontAwesomeIcon icon={user.isActive ? faCheck : faTimes} />
                  {user.isActive ? 'Active' : 'Inactive'}
                </StatusBadge>
              </TableCell>
              <TableCell>
                {new Date(user.subscriptionStartDate).toLocaleDateString()}
                {user.subscriptionEndDate && ` - ${new Date(user.subscriptionEndDate).toLocaleDateString()}`}
              </TableCell>
              <ActionCell>
                <EditButton onClick={() => handleOpenModal(user)}>
                  <FontAwesomeIcon icon={faEdit} />
                </EditButton>
                <DeleteButton onClick={() => handleDeleteClick(user)}>
                  <FontAwesomeIcon icon={faTrash} />
                </DeleteButton>
                <ReportButton onClick={() => handleViewReport(user.id)}>
                  <FontAwesomeIcon icon={faChartLine} />
                </ReportButton>
              </ActionCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
      
      {isModalOpen && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {currentUser ? 'Edit Business User' : 'Add Business User'}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName || ''}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="phoneNumber">Phone Number</Label>
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="text"
                  value={formData.phoneNumber || ''}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              {!currentUser && (
                <FormGroup>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password || ''}
                    onChange={handleInputChange}
                    required={!currentUser}
                  />
                </FormGroup>
              )}
              
              <FormGroup>
                <Label htmlFor="planId">Business Plan</Label>
                <Select
                  id="planId"
                  name="planId"
                  value={formData.planId || ''}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a plan</option>
                  {businessPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} (${plan.price}{plan.period ? `/${plan.period}` : ''})
                    </option>
                  ))}
                </Select>
              </FormGroup>
              
              <SubmitButton type="submit">
                {currentUser ? 'Update Business User' : 'Create Business User'}
              </SubmitButton>
            </Form>
          </ModalContent>
        </Modal>
      )}
      
      {isConfirmModalOpen && userToDelete && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ConfirmationTitle>Confirm Delete</ConfirmationTitle>
            <ConfirmationText>
              Are you sure you want to delete the business user "{userToDelete.companyName}"?
              This action cannot be undone.
            </ConfirmationText>
            <ConfirmationButtons>
              <CancelButton onClick={() => setIsConfirmModalOpen(false)}>
                Cancel
              </CancelButton>
              <ConfirmButton onClick={handleConfirmDelete}>
                Delete
              </ConfirmButton>
            </ConfirmationButtons>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </>
  );
};

export default BusinessUsersPage; 